import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  DespachoInfoCallout,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { formatIsoDateMx } from "@/lib/dateMx";

const DISPATCH_LABELS = {
  scheduled: "Programado",
  due: "Listo para enviar",
  sent: "Enviado",
};

const DISPATCH_VARIANTS = {
  scheduled: "secondary",
  due: "default",
  sent: "outline",
};

const KIND_COPY = {
  compliance: {
    title: "Declaraciones por vencer",
    info:
      "Recordatorios de obligaciones fiscales agrupadas (SAT, IMSS, REPSE…). Indican qué impuestos y qué empresas deben declarar en el periodo.",
    empty:
      "No hay recordatorios de declaraciones precargados. Genere vencimientos, asigne auxiliares y configure grupos de obligaciones en Calendario.",
  },
  task: {
    title: "Actividades del despacho",
    info:
      "Recordatorios de tareas operativas y plantillas configuradas en Calendario → Recordatorios internos. No incluye declaraciones fiscales.",
    empty:
      "No hay recordatorios de actividades precargados. Cree tareas internas con fecha de vencimiento, auxiliar asignado o plantillas operativas en Calendario.",
  },
};

function groupByAuxiliar(items) {
  const map = new Map();
  for (const row of items) {
    const key = row.recipient_user_id || row.recipient_name || "—";
    if (!map.has(key)) {
      map.set(key, {
        recipient_user_id: row.recipient_user_id,
        recipient_name: row.recipient_name || "—",
        items: [],
      });
    }
    map.get(key).items.push(row);
  }
  return [...map.values()].sort((a, b) =>
    String(a.recipient_name).localeCompare(String(b.recipient_name), "es"),
  );
}

function ScheduledRemindersList({
  readOnly = false,
  dateFrom = "",
  dateTo = "",
  periodLabel = "",
  reminderKind = "compliance",
  autoPreload = true,
}) {
  const copy = KIND_COPY[reminderKind] || KIND_COPY.compliance;
  const [board, setBoard] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [preloading, setPreloading] = useState(false);

  const loadBoard = useCallback(async () => {
    setLoadState("loading");
    try {
      const params = { audience: "auxiliar", reminder_kind: reminderKind };
      if (dateFrom) params.scheduled_from = dateFrom;
      if (dateTo) params.scheduled_to = dateTo;
      const data = await despachoFiscalInfoApi.getScheduledRemindersBoard(params);
      setBoard(data);
      setLoadState("ready");
    } catch (err) {
      setBoard(null);
      setLoadState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, [dateFrom, dateTo, reminderKind]);

  const preload = useCallback(async () => {
    if (readOnly) return;
    setPreloading(true);
    try {
      const result = await despachoFiscalInfoApi.preloadScheduledReminders();
      const created = (result.alerts_created ?? 0) + (result.drafts_created ?? 0)
        + (result.operational_alerts_created ?? 0);
      const cancelled = (result.cancelled_previous ?? 0) + (result.cancelled_operational_previous ?? 0);
      if (created > 0 || cancelled > 0) {
        const opPart = result.operational_alerts_created
          ? ` · ${result.operational_alerts_created} operativo(s)`
          : "";
        toast.success(
          `Precargados ${result.alerts_created ?? 0} recordatorio(s)${opPart}${cancelled ? ` · ${cancelled} reagrupado(s)` : ""}.`,
        );
      } else {
        toast.message("No hay recordatorios nuevos que precargar en la ventana configurada.");
      }
      await loadBoard();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setPreloading(false);
    }
  }, [loadBoard, readOnly]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (readOnly || !autoPreload || reminderKind !== "compliance") {
        await loadBoard();
        return;
      }
      setPreloading(true);
      try {
        await despachoFiscalInfoApi.preloadScheduledReminders();
      } catch {
        /* mostrar tablero aunque falle la precarga */
      } finally {
        if (!cancelled) setPreloading(false);
      }
      if (!cancelled) await loadBoard();
    })();
    return () => {
      cancelled = true;
    };
  }, [autoPreload, loadBoard, readOnly, reminderKind]);

  const groups = useMemo(() => groupByAuxiliar(board?.items || []), [board?.items]);

  const columns = [
    {
      key: "scheduled_for",
      label: "Fecha envío",
      render: (r) => formatIsoDateMx(r.scheduled_for) || "—",
    },
    {
      key: "dispatch_state",
      label: "Estado",
      render: (r) => (
        <DespachoStatusBadge variant={DISPATCH_VARIANTS[r.dispatch_state] || "secondary"}>
          {DISPATCH_LABELS[r.dispatch_state] || r.status || "—"}
        </DespachoStatusBadge>
      ),
    },
    {
      key: "message",
      label: "Mensaje",
      render: (r) => {
        const text = r.message || "—";
        const family = r.reminder_bundle_group_id && !String(r.reminder_bundle_group_id).startsWith("obl:")
          ? ` [${r.bundled_family ? "grupo" : "agrupado"}]`
          : "";
        return `${text.slice(0, 120)}${family && text.length <= 120 ? family : ""}`;
      },
    },
  ];

  if (loadState === "loading" || preloading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        {preloading ? "Precargando recordatorios…" : "Cargando recordatorios…"}
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <DespachoSectionCard title={copy.title}>
        <p className="text-sm text-rose-800 m-0 mb-2">No se pudo cargar el tablero.</p>
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={loadBoard}>
          Reintentar
        </Button>
      </DespachoSectionCard>
    );
  }

  const summary = board?.summary || {};

  return (
    <div className="space-y-4" data-testid={`scheduled-reminders-${reminderKind}`}>
      <DespachoInfoCallout>{copy.info}</DespachoInfoCallout>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-600 m-0">
          {summary.total ?? 0} recordatorio(s) · {summary.scheduled_future ?? 0} programado(s) ·{" "}
          {summary.due_pending ?? 0} listo(s) para enviar · {periodLabel || "Periodo filtrado"}
        </p>
        <div className="flex gap-2">
          {!readOnly && reminderKind === "compliance" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-8"
              onClick={preload}
              disabled={preloading}
            >
              Actualizar precarga
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="ghost" className="rounded-sm h-8" onClick={loadBoard}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
            Refrescar
          </Button>
        </div>
      </div>

      {groups.length ? (
        groups.map((group) => (
          <DespachoSectionCard
            key={group.recipient_user_id || group.recipient_name}
            title={group.recipient_name}
            description={`${group.items.length} recordatorio(s)`}
          >
            <DespachoSimpleTable
              columns={columns}
              rows={group.items.map((r) => ({ ...r, id: r.id }))}
              emptyLabel="Sin recordatorios."
            />
          </DespachoSectionCard>
        ))
      ) : (
        <DespachoSectionCard title={copy.title}>
          <p className="text-sm text-slate-600 m-0">{copy.empty}</p>
        </DespachoSectionCard>
      )}
    </div>
  );
}

export default function ScheduledAuxRemindersPanel(props) {
  const { nested = true, ...rest } = props;

  if (!nested) {
    return <ScheduledRemindersList {...rest} />;
  }

  return (
    <Tabs defaultValue="compliance" className="space-y-4">
      <TabsList className="rounded-sm h-9">
        <TabsTrigger value="compliance" className="rounded-sm text-xs">
          Declaraciones por vencer
        </TabsTrigger>
        <TabsTrigger value="task" className="rounded-sm text-xs">
          Actividades del despacho
        </TabsTrigger>
      </TabsList>
      <TabsContent value="compliance" className="mt-0">
        <ScheduledRemindersList {...rest} reminderKind="compliance" />
      </TabsContent>
      <TabsContent value="task" className="mt-0">
        <ScheduledRemindersList {...rest} reminderKind="task" autoPreload={false} />
      </TabsContent>
    </Tabs>
  );
}
